import { useQuery } from "react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { useDataProvider } from "react-admin";
import { formatInstant } from "../../utils/timezone";

const formatDate = (value) => {
  if (!value) return "-";
  return formatInstant(value, { showTime: true });
};

const ageLabel = (value) => {
  if (!value) return "Belum ada";
  const ageSeconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (ageSeconds < 60) return `${ageSeconds} detik lalu`;
  const minutes = Math.floor(ageSeconds / 60);
  if (minutes < 60) return `${minutes} menit lalu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} jam lalu`;
  return `${Math.floor(hours / 24)} hari lalu`;
};

const statusConfig = {
  healthy: { label: "Healthy", color: "success" },
  warning: { label: "Warning", color: "warning" },
  error: { label: "Error", color: "error", sx: { backgroundColor: "#b71c1c", color: "#fff" } },
  offline: { label: "Offline", color: "default", sx: { backgroundColor: "#455a64", color: "#fff" } },
  never_connected: { label: "Never connected", color: "default", sx: { backgroundColor: "#eceff1", color: "#546e7a" } },
};

const StatusChip = ({ status }) => {
  const config = statusConfig[status] || statusConfig.never_connected;
  return <Chip size="small" label={config.label} color={config.color} sx={config.sx} />;
};

const SyncMonitorList = () => {
  const deploymentRole = String(process.env.REACT_APP_DEPLOYMENT_ROLE || "depo").toLowerCase();
  const isHO = deploymentRole === "ho" || deploymentRole === "central";
  const dataProvider = useDataProvider();
  const {
    data: statusData,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery(
    ["sync-status", "monitor"],
    () => dataProvider.getListNoParams("sync/status"),
    { enabled: isHO, refetchInterval: isHO ? 10000 : false }
  );
  const statuses = statusData?.data || statusData || [];
  const summaryStatuses = ["healthy", "warning", "error", "offline", "never_connected"];

  const refreshMonitor = () => {
    if (isHO) refetchStatus();
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: "auto" }}>
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SyncAltIcon />
              <Box>
                <Typography variant="h5">{isHO ? "Monitoring Sync Depo" : "Status Sync Lokal"}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {isHO
                    ? "Pantau kesehatan worker dan waktu sync data setiap depo secara realtime."
                    : "Status koneksi worker dipantau dari server HO; antrean sync lokal tersedia di bawah."}
                </Typography>
              </Box>
            </Stack>
            <Button startIcon={<RefreshIcon />} onClick={refreshMonitor} disabled={isHO && statusLoading}>
              Refresh
            </Button>
          </Stack>

          {isHO ? <>
          <Grid container spacing={2}>
            {summaryStatuses.map((status) => (
              <Grid item xs={12} sm={6} md={2.4} key={status}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <StatusChip status={status} />
                    <Typography variant="h4">
                      {statuses.filter((row) => row.status === status).length}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Alert severity="info">
            Worker dianggap warning setelah 2 menit tanpa heartbeat dan offline setelah 5 menit.
            Depo tanpa transaksi baru tetap sehat selama heartbeat berjalan.
          </Alert>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Depo</TableCell>
                <TableCell>Worker terakhir terlihat</TableCell>
                <TableCell>Depo → HO</TableCell>
                <TableCell>HO → Depo</TableCell>
                <TableCell>Pending / Error / Conflict</TableCell>
                <TableCell>Error terakhir</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statuses.map((row) => (
                <TableRow key={`${row.node_id || "unknown"}-${row.company_id || "unknown"}`}>
                  <TableCell><StatusChip status={row.status} /></TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.company_name || "Unknown depo"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.node_id || "node belum diketahui"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.timezone || "timezone belum diketahui"}
                    </Typography>
                  </TableCell>
                  <TableCell title={formatDate(row.last_seen_at)}>{ageLabel(row.last_seen_at)}</TableCell>
                  <TableCell title={formatDate(row.last_push_data_at)}>{ageLabel(row.last_push_data_at)}</TableCell>
                  <TableCell title={formatDate(row.last_pull_data_at)}>{ageLabel(row.last_pull_data_at)}</TableCell>
                  <TableCell>{row.pending_events || 0} / {row.failed_events || 0} / {row.conflict_events || 0}</TableCell>
                  <TableCell sx={{ maxWidth: 360, whiteSpace: "normal" }}>{row.last_error || "-"}</TableCell>
                </TableRow>
              ))}
              {!statusLoading && statuses.length === 0 ? (
                <TableRow><TableCell colSpan={7}>Belum ada data status depo.</TableCell></TableRow>
              ) : null}
            </TableBody>
          </Table>
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Last Synced Transaction</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Depo</TableCell>
                  <TableCell>Depo -&gt; HO</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Synced</TableCell>
                  <TableCell>HO -&gt; Depo</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Synced</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statuses.map((row) => {
                  const push = row.last_push_transaction;
                  const pull = row.last_pull_transaction;
                  return (
                    <TableRow key={`transaction-${row.node_id || "unknown"}-${row.company_id || "unknown"}`}>
                      <TableCell>{row.company_name || "Unknown depo"}</TableCell>
                      <TableCell>{push?.transaction_code || push?.table_name || "-"}</TableCell>
                      <TableCell>{formatDate(push?.created_at)}</TableCell>
                      <TableCell title={formatDate(row.last_push_data_at)}>{ageLabel(row.last_push_data_at)}</TableCell>
                      <TableCell>{pull?.transaction_code || pull?.table_name || "-"}</TableCell>
                      <TableCell>{formatDate(pull?.created_at)}</TableCell>
                      <TableCell title={formatDate(row.last_pull_data_at)}>{ageLabel(row.last_pull_data_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          </> : (
            <Alert severity="info">
              Halaman ini berjalan pada database depo. Worker mengirim heartbeat dan status koneksi ke server HO,
              sehingga status koneksi pada halaman local tidak digunakan sebagai indikator koneksi ke HO.
              Periksa halaman Monitoring Sync di HO untuk status Healthy, Warning, Error, atau Offline.
            </Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default SyncMonitorList;
